import { Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { useStoreInfo } from '@/features/storefront/hooks/useCatalog';

import { Logo } from './Logo';
import { Reveal } from './Reveal';
import {
  FacebookIcon,
  InstagramIcon,
  TikTokIcon,
  XIcon,
  YouTubeIcon,
} from './social-icons';

// Which social platforms the footer can render. Each shows only when the admin
// has set its URL in Settings → Socials.
const SOCIAL_DEFS = [
  { key: 'instagram', label: 'Instagram', Icon: InstagramIcon },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon },
  { key: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
  { key: 'youtube', label: 'YouTube', Icon: YouTubeIcon },
  { key: 'twitter', label: 'X', Icon: XIcon },
];

// Only render admin-supplied links that are real http(s) URLs — never a
// javascript:/data: URI (defence-in-depth against a hostile settings value).
function isSafeHttpUrl(value) {
  try {
    const { protocol } = new URL(value);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

const columns = [
  {
    title: 'Shop',
    links: [
      { label: 'All Fragrances', to: routes.collection },
      { label: 'Best Sellers', to: `${routes.collection}?sort=best` },
      { label: 'New Arrivals', to: `${routes.collection}?filter=new` },
      { label: 'Gift Sets', to: routes.collection },
    ],
  },
  {
    title: 'The House',
    links: [
      { label: 'Our Story', to: routes.about },
      { label: 'Craftsmanship', to: routes.about },
      { label: 'Sustainability', to: routes.about },
      { label: 'Journal', to: routes.about },
    ],
  },
  {
    title: 'Customer Care',
    links: [
      { label: 'Contact', to: routes.contact },
      { label: 'Terms & Conditions', to: routes.terms },
      { label: 'Privacy Policy', to: routes.privacy },
    ],
  },
];

export function Footer() {
  const { data: storeInfo } = useStoreInfo();

  const socials = SOCIAL_DEFS.filter((s) => isSafeHttpUrl(storeInfo?.socials?.[s.key])).map((s) => ({
    label: s.label,
    Icon: s.Icon,
    href: storeInfo.socials[s.key],
  }));
  if (storeInfo?.email) {
    socials.push({ label: 'Email us', Icon: Mail, href: `mailto:${storeInfo.email}` });
  }

  return (
    <footer className="border-t border-[color:var(--color-line)] bg-[color:var(--color-cream)]">
      <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 lg:py-20">
        <Reveal className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
          {/* Brand */}
          <div className="max-w-sm">
            <Logo />
            <p className="mt-5 text-sm leading-6 text-[color:var(--color-body)]">
              Extrait de parfum, composed in small batches to linger longer than the evening. A
              house of never-ending scent.
            </p>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className="eyebrow text-[color:var(--color-ink)]">{col.title}</h3>
              <ul className="mt-5 flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="hover:text-gold-700 text-sm text-[color:var(--color-body)] transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </Reveal>

        {/* Gold hairline divider */}
        <div className="via-gold-300 mt-14 h-px w-full bg-gradient-to-r from-transparent to-transparent" />

        <Reveal
          delay={0.1}
          className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row"
        >
          <div className="flex flex-col items-center gap-1 sm:items-start">
            <p className="text-xs text-[color:var(--color-muted)]">
              © {new Date().getFullYear()} {storeInfo?.name ?? 'Ayat Fragrances'}. All rights
              reserved.
            </p>
            <p className="text-xs text-[color:var(--color-muted)]">
              Powered by{' '}
              <a
                href="https://bitsphere.site/"
                target="_blank"
                rel="noreferrer noopener"
                className="hover:text-gold-700 font-medium text-[color:var(--color-body)] underline-offset-2 transition-colors hover:underline"
              >
                BitSphere
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {socials.map(({ label, Icon, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={label}
                className="hover:border-gold-400 hover:text-gold-700 inline-flex size-9 items-center justify-center rounded-full border border-[color:var(--color-line)] text-[color:var(--color-body)] transition"
              >
                <Icon size={16} strokeWidth={1.6} />
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </footer>
  );
}
